import { HighlightRange } from "components/dashboard/product-template-table/utils/interfaces";
import { DatasetFullDefaultConfig } from "models/dataset-models";
import { Button } from "primereact/button";
import { ColorPicker } from "primereact/colorpicker";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React, { useState } from "react";
import { T } from "services/lang/LanguageService";

interface HighlightEditorProps {
  identifier: string;
  currentConfig: DatasetFullDefaultConfig;
  setCurrentConfig: React.Dispatch<
    React.SetStateAction<DatasetFullDefaultConfig>
  >;
  onConfigChange: any;
}

const HighlightsEditor = ({
  currentConfig,
  setCurrentConfig,
  onConfigChange,
  identifier,
}: HighlightEditorProps) => {
  const getHighlightRanges = () => {
    if (currentConfig.datasets) {
      return currentConfig.datasets?.filter(
        (dataset) => dataset.identifier === identifier
      )[0].highlightRanges;
    } 
    return currentConfig.highlightRanges;
    
  };

  const [highlightRanges, setHighlightRanges] = useState<HighlightRange[]>(
    getHighlightRanges() ?? []
  );

  const onAddHighlightRange = () => {
    const highlightRangesToUpdate = structuredClone(highlightRanges);
    highlightRangesToUpdate.push({
      color: "#000000",
      bgColor: "#FFFFFF",
      min: { value: 0, inclusive: true },
      max: { value: 0, inclusive: true },
    });
    if (currentConfig.datasets) {
      const datasetsToUpdate = [...currentConfig.datasets!];
      const datasetToUpdate = {
        ...datasetsToUpdate.filter(
          (dataset) => dataset.identifier === identifier
        )[0],
      };
      datasetToUpdate.highlightRanges = highlightRangesToUpdate;
      datasetsToUpdate[
        datasetsToUpdate.indexOf(
          datasetsToUpdate.filter(
            (dataset) => dataset.identifier === identifier
          )[0]
        )
      ] = datasetToUpdate;
      setHighlightRanges(highlightRangesToUpdate);
      setCurrentConfig({ ...currentConfig, datasets: datasetsToUpdate });
      onConfigChange({ ...currentConfig, datasets: datasetsToUpdate });
    } else {
      const datasetToUpdate = [currentConfig];
      datasetToUpdate[0].highlightRanges = highlightRangesToUpdate;

      setHighlightRanges(highlightRangesToUpdate);
      setCurrentConfig(datasetToUpdate[0]);
    }
  };

  const onRowEditComplete = (e: any) => {
    if (currentConfig.datasets) {
      const datasetsToUpdate = [...currentConfig.datasets!];
      const { newData, index } = e;
      const datasetToUpdate = {
        ...datasetsToUpdate.filter(
          (dataset) => dataset.identifier === identifier
        )[0],
      };

      const highlightRangesToUpdate = structuredClone(highlightRanges);
      highlightRangesToUpdate[index] = newData;
      datasetToUpdate.highlightRanges = highlightRangesToUpdate;
      datasetsToUpdate[
        datasetsToUpdate.indexOf(
          datasetsToUpdate.filter(
            (dataset) => dataset.identifier === identifier
          )[0]
        )
      ] = datasetToUpdate;
      setHighlightRanges(highlightRangesToUpdate);

      setCurrentConfig({ ...currentConfig, datasets: datasetsToUpdate });
      onConfigChange({ ...currentConfig, datasets: datasetsToUpdate });
    } else {
      const datasetToUpdate = [currentConfig];
      const { newData, index } = e;
      const highlightRangesToUpdate = structuredClone(highlightRanges);
      highlightRangesToUpdate[index] = newData;
      datasetToUpdate[0].highlightRanges = highlightRangesToUpdate;

      setHighlightRanges(highlightRangesToUpdate);
      setCurrentConfig(datasetToUpdate[0]);
    }
  };

  const numberEditor = (options: any, field: string) => {
    return (
      <InputNumber
        value={options.value.value}
        onChange={(e) =>
          options.editorCallback({ ...options.rowData[field], value: e.value })
        }
        inputStyle={{ width: "100%" }}
      />
    );
  };

  const switchEditor = (options: any, field: string) => {
    return (
      <InputSwitch
        checked={options.value.inclusive}
        onChange={(e) =>
          options.editorCallback({
            ...options.rowData[field],
            inclusive: e.value,
          })
        }
      />
    );
  };

  const colorEditor = (options: any) => {
    return (
      <div className="p-inputgroup">
        <InputText
          value={options.value}
          onChange={(e) => options.editorCallback(e.target.value)}
          style={{ width: "100%" }}
        />
        <ColorPicker
          className="p-inputgroup-addon p-0"
          value={options.value}
          onChange={(e) => options.editorCallback(`#${e.value}`)}
          style={{ width: "50%" }}
        />
      </div>
    );
  };

  const colorBodyTemplate = (rowData: any, field: string) => {
    return (
      <div className="flex justify-content-center">
        {field === "color" ? (
          <div
            style={{
              backgroundColor: rowData.color,
              width: "25px",
              height: "25px",
              border: "1px solid black",
            }}
          />
        ) : (
          <div
            style={{
              backgroundColor: rowData.bgColor,
              width: "25px",
              height: "25px",
              border: "1px solid black",
            }}
          />
        )}
      </div>
    );
  };

  const deleteButtonBodyTemplate = (rowData: any, index: number) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-danger p-button-text"
        onClick={() => {
          const updatedHighlightRanges = [...highlightRanges];
          updatedHighlightRanges.splice(index, 1);
          setHighlightRanges(updatedHighlightRanges);
          if (currentConfig.datasets) {
            setCurrentConfig({
              ...currentConfig,
              datasets: currentConfig.datasets?.map((dataset) => {
                if (dataset.identifier === identifier) {
                  dataset.highlightRanges = updatedHighlightRanges;
                }
                return dataset;
              }),
            });
          } else {
            const datasetToUpdate = [currentConfig];
            datasetToUpdate[0].highlightRanges = updatedHighlightRanges;
            setCurrentConfig(datasetToUpdate[0]);
          }
        }}
      />
    );
  };

  const tableHeader = (
    <div style={{ textAlign: "left" }}>
      <Button
        icon="pi pi-plus"
        label={T("action.add-highlight-range")}
        className="component-editor-button icon-green"
        onClick={onAddHighlightRange}
      />
    </div>
  );

  return (
      <DataTable
        value={highlightRanges}
        editMode="row"
        onRowEditComplete={onRowEditComplete}
        header={tableHeader}
        tableStyle={{ width: "100%" }}
      >
        <Column
          className="p-0"
          field="min"
          header="Min"
          align="center"
          editor={(options) => numberEditor(options, "min")}
          body={(rowData: any) => rowData.min.value}
          style={{ width: "10%" }}
        />
        <Column
          className="p-0"
          field="min"
          header={T("word.inclusive-min")}
          align="center"
          editor={(options) => switchEditor(options, "min")}
          body={(rowData: any) => rowData.min.inclusive}
        />
        <Column
          className="p-0"
          field="max"
          header="Max"
          align="center"
          editor={(options) => numberEditor(options, "max")}
          body={(rowData: any) => rowData.max.value}
          style={{ width: "10%" }}
        />
        <Column
          className="p-0"
          field="max"
          header={T("word.inclusive-max")}
          align="center"
          editor={(options) => switchEditor(options, "max")}
          body={(rowData: any) => rowData.max.inclusive}
        />
        <Column
          className="p-0"
          field="color"
          header={T("word.color")}
          align="center"
          editor={colorEditor}
          body={(rowData) => colorBodyTemplate(rowData, "color")}
        />
        <Column
          className="p-0"
          field="bgColor"
          header={T("word.background-color")}
          align="center"
          editor={colorEditor}
          body={(rowData) => colorBodyTemplate(rowData, "bgColor")}
        />
        <Column
          className="p-0"
          rowEditor
          style={{ width: "10%" }}
          headerStyle={{ width: "10%" }}
          bodyStyle={{ textAlign: "center" }}
        />
        <Column
          className="p-0"
          body={(rowData: any) =>
            deleteButtonBodyTemplate(rowData, highlightRanges.indexOf(rowData))
          }
          style={{ width: "10%" }}
        />
      </DataTable>
  );
};

export default HighlightsEditor;
